import { useState, useEffect } from "react";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Loader2, Radar, CheckCircle2, XCircle } from "lucide-react";
import { toast } from "sonner";

const Mark = ({ ok }) => ok ? <CheckCircle2 size={15} className="text-emerald-600" /> : <XCircle size={15} className="text-red-600" />;
const Cell = ({ r }) => (
  <span className="text-xs text-muted-foreground">{r.count} block(s){r.types.length ? ` · ${[...new Set(r.types)].join(", ")}` : ""}{r.invalid ? ` · ${r.invalid} invalid` : ""}{r.error ? ` · ${r.error}` : ""}</span>
);

export function RawHtmlCheck() {
  const [paths, setPaths] = useState("");
  const [run, setRun] = useState(null);
  const [busy, setBusy] = useState(false);
  useEffect(() => { api.get("/admin/seo/schema-manager/raw-check/latest").then((r) => r.data?.id && setRun(r.data)).catch(() => {}); }, []);
  const go = async () => {
    setBusy(true);
    try {
      const list = paths.split(/\n/).map((x) => x.trim()).filter(Boolean);
      const r = await api.post("/admin/seo/schema-manager/raw-check", { paths: list, sample: 10 });
      setRun(r.data);
    } catch { toast.error("Check failed"); } finally { setBusy(false); }
  };
  return (
    <section data-testid="schema-raw-check">
      <h3 className="font-bold text-steel flex items-center gap-2 mb-1"><Radar size={18} /> Raw HTML schema detection (live site)</h3>
      <p className="text-xs text-muted-foreground mb-3">Fetches live URLs the way the Schema.org Validator does (plain HTTP, no JavaScript) and the way Googlebot does (rendered by the crawler cache). Leave empty to test the homepage + 10 random products.</p>
      <Textarea rows={3} value={paths} onChange={(e) => setPaths(e.target.value)} placeholder={"/product/your-slug\n/category/Eftpos"} className="font-mono text-xs" data-testid="schema-raw-paths" />
      <Button size="sm" className="mt-2 bg-steel text-white hover:bg-steel/90" onClick={go} disabled={busy} data-testid="schema-raw-run">
        {busy ? <Loader2 size={14} className="mr-1.5 animate-spin" /> : <Radar size={14} className="mr-1.5" />} Run detection
      </Button>
      {run && (
        <div className="mt-3 space-y-2">
          <div className="text-sm text-steel" data-testid="schema-raw-summary">
            Schema.org Validator view: <b>{run.validator_pass}/{run.total}</b> pass · Google rendered view: <b>{run.google_pass}/{run.total}</b> pass
            <span className="text-xs text-muted-foreground"> · {new Date(run.at).toLocaleString()}</span>
          </div>
          <div className="rounded-xl border bg-white divide-y max-h-[50vh] overflow-y-auto" data-testid="schema-raw-rows">
            {run.rows.map((r) => (
              <div key={r.url} className="p-3 text-sm">
                <a href={r.url} target="_blank" rel="noreferrer" className="font-semibold text-steel hover:text-accent break-all">{r.url}</a>
                <div className="flex items-center gap-2 mt-1"><Mark ok={r.validator_ok} /> <span className="text-xs font-semibold">Raw HTML</span> <Cell r={r.plain} /></div>
                <div className="flex items-center gap-2"><Mark ok={r.google_ok} /> <span className="text-xs font-semibold">Googlebot</span> <Cell r={r.bot} /></div>
              </div>
            ))}
          </div>
        </div>
      )}
    </section>
  );
}
